import { useCallback, useEffect, useState } from 'react';
import { NoData, Spin } from '@wajeez-web/components/ui';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { getLocalTimeFromUTC } from '@wajeez-web/core/utils';
import {
  useBusinessDispatch,
  useBusinessSelector,
  BusinessActions,
  BusinessUsersActions,
} from '@wajeez-web/stores/business-group';
import { Server } from '@wajeez/wajeez.microservices';
import { ConfirmationModal } from '@wajeez-web/components/ui';

interface UserBusinessProps {
  userId: string | undefined;
}

export const UserBusiness = ({ userId }: UserBusinessProps) => {
  const businessDispatch = useBusinessDispatch();

  const [unlinkUserConfirmationModal, setUnlinkUserConfirmationModal] =
    useState<{ isOpen: boolean; message: string }>();
  const [selectedBusinessGroup, setSelectedBusinessGroup] = useState<{
    id: number;
  }>();

  const businessGroups = useBusinessSelector(
    ({ businessGroups }) => businessGroups.businessGroupsByUserId
  );

  const unlinkUserStatus = useBusinessSelector(
    ({ businessGroupsUsers }) =>
      businessGroupsUsers.unlinkBusinessGroupUser.status
  );

  const getUserBusinessGroups = useCallback(() => {
    userId &&
      businessDispatch(BusinessActions.getBusinessGroupsByUserIdAsync(userId));
  }, [businessDispatch, userId]);

  useEffect(() => {
    getUserBusinessGroups();
  }, [getUserBusinessGroups]);

  return (
    <div>
      <div className=" divide-y overflow-clip ">
        <div className="grid grid-cols-4 items-center gap-x-2 gap-y-2 py-2 text-center">
          <div className="px-1">
            <ReturnTranslatedText
              origintext="Name"
              text={`tables.columnTitles.${'Name'}`}
            />
          </div>
          <div className="px-1">
            <ReturnTranslatedText
              origintext="User State"
              text={`tables.columnTitles.${'User State'}`}
            />
          </div>
          <div className="px-1">
            <ReturnTranslatedText
              origintext="Expiration Date"
              text={`tables.columnTitles.${'Expiration Date'}`}
            />
          </div>
          <div className="px-1"></div>
        </div>
        {businessGroups.status === 'loading' ? (
          <div className="w-full pt-20">
            <Spin />
          </div>
        ) : businessGroups.data.length ? (
          businessGroups.data.map((item, i) => (
            <div className="flex justify-center items-center py-2 divide-s text-center break-words tex-xs">
              <div className="w-1/4 px-2 py-2">{item.name}</div>
              <div className="w-1/4 px-2 py-2">
                {item.userState !== undefined
                  ? Server.UserState[item.userState]
                  : '_'}
              </div>
              <div className="w-1/4 px-2 py-2 flex space-s-2 align-middle items-center justify-center">
                <div>
                  {(item.expiresOn && getLocalTimeFromUTC(item.expiresOn)) ||
                    '-'}
                </div>
              </div>
              <div
                className="w-1/4 px-2 py-2 text-red-500 cursor-pointer"
                onClick={() => {
                  item.id && setSelectedBusinessGroup({ id: item.id });

                  setUnlinkUserConfirmationModal({
                    isOpen: true,
                    message:
                      'Are you sure that you want to unlink this business group?',
                  });
                }}
              >
                <ReturnTranslatedText
                  origintext="Unlink"
                  text={`buttons.${'Unlink'}`}
                />
              </div>
            </div>
          ))
        ) : (
          <div className="w-full pt-20">
            <NoData />
          </div>
        )}
      </div>
      {unlinkUserConfirmationModal?.isOpen && (
        <ConfirmationModal
          isOpen={unlinkUserConfirmationModal.isOpen}
          message={unlinkUserConfirmationModal.message}
          onClose={() => {
            setUnlinkUserConfirmationModal({ isOpen: false, message: '' });
          }}
          onSubmit={async () => {
            const res = await businessDispatch(
              BusinessUsersActions.unlinkBusinessGroupUserAsync({
                businessGroupId: selectedBusinessGroup?.id,
                userId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setUnlinkUserConfirmationModal({ isOpen: false, message: '' });

              getUserBusinessGroups();
            }
          }}
          isLoading={unlinkUserStatus === 'loading'}
        />
      )}
    </div>
  );
};
